/**
 * usePublicContent — fetches CMS content from MongoDB for the public website.
 *
 * The website normally reads from static i18n JSON files. When the founder
 * edits content in the admin CMS, those edits are saved to MongoDB. This hook
 * fetches that DB content and returns a merged object that overlays DB values
 * on top of the i18n defaults — so the site always has fallback copy even if
 * the DB is empty or the API is unreachable.
 *
 * Usage:
 *   const { hero, testimonials, products } = usePublicContent(language);
 *
 * Each value is the merged result: i18n defaults + any DB overrides.
 * Loading/error states are provided for optional UX use (the site degrades
 * gracefully to i18n defaults on any failure).
 */

import { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

export function usePublicContent(language = 'it') {
  const [dbContent, setDbContent] = useState(null);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);

  // Always fetch fresh on language change — no module-level cache so that
  // CMS edits appear on the next page load without clearing browser state.
  useEffect(() => {
    setLoading(true);
    axios
      .get(`${API_URL}/content/public`, { params: { language } })
      .then(res => {
        const items = res.data?.content || [];
        const grouped = {};
        items.forEach(item => {
          if (!grouped[item.section]) grouped[item.section] = [];
          grouped[item.section].push(item);
        });
        setDbContent(grouped);
      })
      .catch(err => {
        // Silent degradation — i18n fallbacks will be used
        console.warn('[usePublicContent] Could not load CMS content:', err.message);
        setError(err.message);
      })
      .finally(() => setLoading(false));
  }, [language]);

  return { dbContent, loading, error };
}

/**
 * mergeHero — overlays a DB hero item's data fields over the i18n hero object.
 * The DB item (if present) is a single Content doc with key='hero_main'.
 */
export function mergeHero(i18nHero, dbContent) {
  // dbContent === null → still loading or fetch failed → use i18n defaults
  // dbContent loaded but no hero key → user hasn't customized → use i18n defaults
  // dbContent has hero key → DB is authoritative
  if (!dbContent?.hero?.length) return i18nHero;
  const dbItem = dbContent.hero.find(x => x.key === 'hero_main');
  if (!dbItem?.data) return i18nHero;
  const merged = { ...i18nHero, ...dbItem.data };
  // Drop location/legal badges from CMS hero_main (Sales QA: no prominent Malta labels)
  if (merged.badge && /malta/i.test(String(merged.badge))) {
    merged.badge = '';
  }
  return merged;
}

/**
 * mergeTestimonials — once the CMS is loaded, the DB is the source of truth.
 * Empty DB testimonials means the founder intentionally removed them — return
 * an empty array so the section disappears from the website. Only fall back
 * to i18n defaults when the API hasn't responded yet (dbContent === null).
 */
export function mergeTestimonials(i18nTestimonials, dbContent) {
  if (dbContent === null) return i18nTestimonials;
  if (!dbContent?.testimonials?.length) return [];
  return dbContent.testimonials
    .slice()
    .sort((a, b) => (a.order || 0) - (b.order || 0))
    .map(item => item.data);
}

/**
 * mergeProducts — overlays DB products data (single doc with key='products_main')
 * over the i18n products object.
 */
export function mergeProducts(i18nProducts, dbContent) {
  if (!dbContent?.products?.length) return i18nProducts;
  const dbItem = dbContent.products.find(x => x.key === 'products_main');
  if (!dbItem?.data) return i18nProducts;
  return { ...i18nProducts, ...dbItem.data };
}
